/**
 * White-label : recalcule la couche « marque » de `src/styles/tokens.css`
 * (`--brand*`) à partir de deux couleurs. Sans valeur, les défauts de
 * tokens.css (DA Encrelio) s'appliquent tels quels.
 *
 * Les variantes sont dérivées par `color-mix` (oklab) : dégradé du panneau
 * de marque, textes posés dessus, action foncée / pâle / bordure. La couleur
 * de marque doit rester assez sombre pour porter du texte blanc (≥ 4,5:1).
 */
export function brandVariables(
  primary?: string,
  accent?: string,
): Record<string, string> {
  const vars: Record<string, string> = {};
  if (primary) {
    vars["--brand"] = primary;
    vars["--brand-mid"] = `color-mix(in oklab, ${primary} 85%, white)`;
    vars["--brand-end"] = `color-mix(in oklab, ${primary} 72%, white)`;
    vars["--brand-on-muted"] = `color-mix(in oklab, white 72%, ${primary})`;
    vars["--brand-on-subtle"] = `color-mix(in oklab, white 58%, ${primary})`;
  }
  if (accent) {
    vars["--brand-action"] = accent;
    vars["--brand-action-strong"] = `color-mix(in oklab, ${accent} 82%, black)`;
    vars["--brand-action-soft"] = `color-mix(in oklab, ${accent} 10%, white)`;
    vars["--brand-action-border"] = `color-mix(in oklab, ${accent} 35%, white)`;
    vars["--brand-action-light"] = `color-mix(in oklab, ${accent} 55%, white)`;
  }
  return vars;
}

/** Pose les variables sur `root` ; une couleur invalide est ignorée. */
export function applyBrand(
  root: HTMLElement,
  primary?: string,
  accent?: string,
  isColor: (value: string) => boolean = (v) => CSS.supports("color", v),
): void {
  const valid = (c?: string) => (c && isColor(c) ? c : undefined);
  const vars = brandVariables(valid(primary), valid(accent));
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
}

const escapeXml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Icône d'une instance sans logo : son initiale, en blanc sur sa couleur. */
export function monogramIcon(name: string, color: string): string {
  const initial = escapeXml(name.trim().charAt(0).toUpperCase() || "?");
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="7" fill="${escapeXml(color)}"/>` +
    `<text x="16" y="22.5" text-anchor="middle" font-family="system-ui, sans-serif" font-size="19" font-weight="700" fill="white">${initial}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Marque blanche : l'onglet porte le nom de l'instance. Avec un logo, il
 * devient l'icône d'onglet et d'écran d'accueil ; sans logo, un autre nom
 * prend son monogramme (jamais l'icône par défaut). Les pages du site public
 * reposent ensuite leur propre titre (`usePageMeta`).
 */
export function applyIdentity(
  doc: Document,
  identity: {
    name: string;
    logoUrl: string | null;
    customName: boolean;
    /** Couleur du monogramme ; par défaut, la marque appliquée (`--brand`). */
    color?: string;
  },
): void {
  doc.title = identity.name;
  const icon = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (identity.logoUrl) {
    icon?.removeAttribute("type");
    icon?.setAttribute("href", identity.logoUrl);
    doc
      .querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
      ?.setAttribute("href", identity.logoUrl);
    return;
  }
  if (!identity.customName || !icon) return;
  const color =
    identity.color ||
    getComputedStyle(doc.documentElement).getPropertyValue("--brand").trim() ||
    "black";
  icon.setAttribute("type", "image/svg+xml");
  icon.setAttribute("href", monogramIcon(identity.name, color));
}
